import React, { useCallback, useEffect, useRef, useState } from "react";
import { SupportAgentTicket } from "@src/types/support";
import styles from "./selected-ticket-view.module.scss";
import { ContainerPanel } from "../container-panel/container-panel";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import AutoTranslationButton from "@src/components/atoms/auto-translation-button/auto-translation-button";
import { ContainerPane } from "../container-pane/container-pane";
import { TicketPageChatHeader } from "@src/components/molecules/ticket-page-ticket-header/ticket-page-ticket-header";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import Spinner from "@src/components/atoms/spinner/spinner";
import { TicketPageChat } from "@src/components/organisms/ticket-page-chat/ticket-page-chat";
import { TicketPatchUtilitiesContainer } from "../ticket-patch-utilities-container/ticket-patch-utilities-container";
import { useI18N } from "@src/language/language-context";
import { getSupportTicketByIdWithLanguageUseCase } from "@src/cloud-connect/support/use-cases/get-support-ticket-by-id-with-language-use-case";
import { useSelector } from "react-redux";
import { RootState } from "@src/reducers/store";
import { useSSEUnavailable } from "@src/cloud-connect/sse/use-sse-unavailable";

interface SelectedTicketViewProps {
  selectedTicket: SupportAgentTicket | null;
}

const cssClassName = "selected-ticket-view";

export const SelectedTicketView = ({
  selectedTicket,
}: SelectedTicketViewProps) => {
  const i18n = useI18N();
  const language = useSelector((state: RootState) => state.yourSlice.language);
  const isSupportActionsDisabled = useSSEUnavailable("service-support");
  const supportSseUnavailableTooltip = isSupportActionsDisabled
    ? i18n("modules.sse.reconnectingActionsUnavailableTooltip")
    : "";
  const [detailedTicket, setDetailedTicket] =
    useState<SupportAgentTicket | null>(null);
  const [detailedTicketLanguage, setDetailedTicketLanguage] =
    useState(language);
  const [hasDetailsError, setHasDetailsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const selectedTicketIdRef = useRef(selectedTicket?.id);
  selectedTicketIdRef.current = selectedTicket?.id;
  const detailedTicketIdRef = useRef(detailedTicket?.id);
  detailedTicketIdRef.current = detailedTicket?.id;
  const ticketDetailsRequestIdRef = useRef(0);
  const latestGetTicketDetailsRef = useRef<(() => Promise<void>) | null>(null);
  const isSelectedTicketLoading =
    isLoading ||
    (selectedTicket !== null &&
      detailedTicket?.id !== selectedTicket.id &&
      !hasDetailsError);
  const isSelectedTicketLanguageCurrent = detailedTicketLanguage === language;
  const isSelectedTicketDetailsOutdated = Boolean(
    selectedTicket &&
      detailedTicket &&
      detailedTicket.id === selectedTicket.id &&
      !isSelectedTicketLanguageCurrent,
  );

  const lastEvent = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEvent"],
  );

  const lastEventAt = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["lastEventAt"],
  );

  const events = useSelector(
    (state: RootState) =>
      state.sseSlice.streams?.["service-support"]?.["events"] ?? {},
  );

  const texts = {
    noTicketsDetails: i18n("modules.selectedTicketView.noTicketsDetails"),
    subheaderText: i18n("modules.selectedTicketView.subheaderText"),
  };

  const getSelectedTicketDetailsById = useCallback(async () => {
    if (!selectedTicket?.id) return;
    const requestId = ++ticketDetailsRequestIdRef.current;
    const requestedTicketId = selectedTicket.id;
    const ticketResponse = await getSupportTicketByIdWithLanguageUseCase(
      requestedTicketId,
      language,
    );

    if (
      requestId !== ticketDetailsRequestIdRef.current ||
      selectedTicketIdRef.current !== requestedTicketId
    ) {
      return;
    }

    if (ticketResponse.success && ticketResponse.data) {
      setIsLoading(false);
      setHasDetailsError(false);
      setDetailedTicket(ticketResponse.data);
      setDetailedTicketLanguage(language);
    } else {
      console.error(
        "Failed to fetch detailed ticket information:",
        ticketResponse.error,
      );
      errorSnackbar(ticketResponse.error);
      setIsLoading(false);
      setHasDetailsError(true);
      if (detailedTicketIdRef.current !== requestedTicketId) {
        setDetailedTicket(null);
      }
    }
  }, [language, selectedTicket?.id]);

  latestGetTicketDetailsRef.current = getSelectedTicketDetailsById;

  // Bumps TicketPageChat's own comments refresh, then awaits the ticket refetch directly
  // (not via the refreshTrigger effect below) so callers like AutoTranslationButton can
  // actually wait for detailedTicket.auto_translate to be up to date.
  const triggerRefresh = useCallback(async () => {
    setRefreshTrigger((n) => n + 1);
    await getSelectedTicketDetailsById();
  }, [getSelectedTicketDetailsById]);

  useEffect(() => {
    if (lastEvent !== "ticket_updated") {
      return;
    }
    const eventPayload = events?.[lastEvent];
    const eventTicketId = eventPayload?.ticket_id;
    // ticket ids are UUID strings: Number() yields NaN on both sides and
    // NaN !== NaN always early-returns, killing the SSE refetch entirely
    if (eventTicketId === null || eventTicketId !== selectedTicket?.id) {
      return;
    }

    void latestGetTicketDetailsRef.current?.();
  }, [events, lastEvent, lastEventAt, selectedTicket?.id]);

  useEffect(() => {
    if (!selectedTicket?.id) {
      ticketDetailsRequestIdRef.current += 1;
      setDetailedTicket(null);
      setHasDetailsError(false);
      setIsLoading(false);
      return;
    }
    if (detailedTicketIdRef.current !== selectedTicket.id) {
      setHasDetailsError(false);
      setIsLoading(true);
    }
    getSelectedTicketDetailsById();
  }, [getSelectedTicketDetailsById, selectedTicket?.id, language]);

  return (
    <div className={styles[cssClassName]}>
      <div
        className={`${styles[`${cssClassName}__column`]} ${
          styles[`${cssClassName}__column--left`]
        }`}
      >
        <ContainerPanel>
          <SubHeader variant={"less-padding"}>
            <FlexRowContainer justifyContent="space-between">
              <SubTitle text={`${texts.subheaderText}${selectedTicket?.id}`} />
              {!isSelectedTicketLoading &&
                isSelectedTicketLanguageCurrent &&
                selectedTicket &&
                detailedTicket &&
                detailedTicket.id === selectedTicket.id && (
                  <AutoTranslationButton
                    key={selectedTicket?.id}
                    ticketId={selectedTicket?.id}
                    backgroundColor="background"
                    ticketAutoTranslationActive={detailedTicket?.auto_translate}
                    callbackForRefresh={triggerRefresh}
                    disabled={isSupportActionsDisabled}
                    disabledTooltip={supportSseUnavailableTooltip}
                  />
                )}
            </FlexRowContainer>
          </SubHeader>
          <ContainerPane>
            <div className={styles[`${cssClassName}__pane-content`]}>
              {isSelectedTicketLoading ? (
                <Spinner />
              ) : detailedTicket !== null &&
                detailedTicket.id === selectedTicket?.id ? (
                <>
                  {isSelectedTicketLanguageCurrent && (
                    <TicketPageChatHeader ticket={detailedTicket} />
                  )}
                  <div className={styles[`${cssClassName}__chat-wrapper`]}>
                    <TicketPageChat
                      key={detailedTicket.id}
                      ticketId={detailedTicket.id}
                      refreshTrigger={refreshTrigger}
                    />
                  </div>
                </>
              ) : (
                <div
                  className={styles[`${cssClassName}__failed-message-wrapper`]}
                >
                  <p className={styles[`${cssClassName}__failed-message`]}>
                    {texts.noTicketsDetails}
                  </p>
                </div>
              )}
            </div>
          </ContainerPane>
        </ContainerPanel>
      </div>

      <div
        className={`${styles[`${cssClassName}__column`]} ${
          styles[`${cssClassName}__column--right`]
        }`}
      >
        <ContainerPanel>
          {isSelectedTicketLoading ||
          (isSelectedTicketDetailsOutdated && !hasDetailsError) ? (
            <Spinner />
          ) : detailedTicket !== null &&
            detailedTicket.id === selectedTicket?.id &&
            isSelectedTicketLanguageCurrent ? (
            <TicketPatchUtilitiesContainer
              key={detailedTicket.id}
              ticket={detailedTicket}
              onTicketUpdated={setDetailedTicket}
            />
          ) : (
            <div className={styles[`${cssClassName}__failed-message-wrapper`]}>
              <p className={styles[`${cssClassName}__failed-message`]}>
                {texts.noTicketsDetails}
              </p>
            </div>
          )}
        </ContainerPanel>
      </div>
    </div>
  );
};
